import { quizConfig } from "../config/quiz";
import type { ProductId, QuizAnswers, TerpeneId } from "../types";

export interface QuizScore {
  terpene: TerpeneId;
  product: ProductId;
  totals: Record<TerpeneId, number>;
}

function answerFor(questionId: string, answerId: string | undefined) {
  const question = quizConfig.questions.find((item) => item.id === questionId);
  return question?.answers.find((item) => item.id === answerId);
}

export function calculateQuizResult(answers: QuizAnswers): QuizScore {
  const terpeneIds = Object.keys(quizConfig.results);
  const totals: Record<TerpeneId, number> = Object.fromEntries(terpeneIds.map((id) => [id, 0]));

  for (const question of quizConfig.questions) {
    const answer = answerFor(question.id, answers[question.id]);
    if (question.type === "terpene" && answer?.terpene) {
      totals[answer.terpene] = (totals[answer.terpene] ?? 0) + (answer.weight ?? 1);
    }
  }

  const topScore = Math.max(...Object.values(totals));
  let tied = terpeneIds.filter((id) => totals[id] === topScore);

  for (const questionId of quizConfig.tieBreakerQuestionIds) {
    if (tied.length === 1) break;
    const tieAnswer = answerFor(questionId, answers[questionId]);
    if (tieAnswer?.terpene && tied.includes(tieAnswer.terpene)) {
      tied = [tieAnswer.terpene];
    }
  }

  const productQuestion = quizConfig.questions.find((question) => question.type === "product");
  const productAnswer = productQuestion ? answerFor(productQuestion.id, answers[productQuestion.id]) : undefined;
  if (!productAnswer?.product) {
    throw new Error("A product-need answer is required.");
  }

  const terpene = tied[0];
  if (!terpene) {
    throw new Error("Terpene answers are required.");
  }

  return { terpene, product: productAnswer.product, totals };
}

export function hasCompleteQuiz(answers: QuizAnswers): boolean {
  return quizConfig.questions.every((question) => Boolean(answers[question.id]));
}
